import * as React from "react";
import { Button, Text, makeStyles } from "@fluentui/react-components";
import { DeleteDismiss24Regular, TableOffsetAdd24Regular } from "@fluentui/react-icons";
import type { QueryAttributeDefinition } from "../../types.js";
import { SelectionPopoverControl } from "../popovers/SelectionPopoverControl.js";

const useStyles = makeStyles({
  selectedSection: {
    display: "grid",
    gap: "6px",
  },
  selectedHeaderRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  selectedList: {
    margin: 0,
    listStyleType: "none",
    paddingLeft: "8px",
    display: "grid",
    gap: "4px",
    maxHeight: "180px",
    overflowY: "auto",
    border: "1px solid var(--colorNeutralStroke2)",
    borderRadius: "6px",
    padding: "8px",
  },
  selectedItem: {
    lineHeight: "1.3",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  selectedItemText: {
    minWidth: 0,
  },
  emptyState: {
    color: "var(--colorNeutralForeground3)",
  },
});

export interface AttributeSelectionControlProps {
  attributes: QueryAttributeDefinition[];
  selectedAttributes: string[];
  onToggleAttribute: (attributeName: string, checked: boolean) => void;
  placeholder?: string;
  selectedLabel?: string;
  emptyLabel?: string;
  showSelector?: boolean;
  showQuickRemove?: boolean;
}

export function AttributeSelectionControl({
  attributes,
  selectedAttributes,
  onToggleAttribute,
  placeholder = "Select attributes",
  selectedLabel = "Selected attributes",
  emptyLabel = "No attributes selected.",
  showSelector = true,
  showQuickRemove = false,
}: AttributeSelectionControlProps): JSX.Element {
  const styles = useStyles();

  const attributesByLogicalName = React.useMemo(
    () =>
      new Map(
        attributes.map((attribute) => [
          attribute.logicalName,
          `${attribute.displayName} (${attribute.logicalName})`,
        ]),
      ),
    [attributes],
  );

  const options = React.useMemo(
    () =>
      attributes.map((attribute) => ({
        key: attribute.logicalName,
        label: `${attribute.displayName} (${attribute.logicalName})`,
        selected: selectedAttributes.includes(attribute.logicalName),
      })),
    [attributes, selectedAttributes],
  );

  return (
    <>
      <div className={styles.selectedSection}>
        <div className={styles.selectedHeaderRow}>
          <Text weight="semibold">
            {selectedLabel} ({selectedAttributes.length})
          </Text>
          {showSelector ? (
            <SelectionPopoverControl
              buttonLabel=""
              buttonIcon={<TableOffsetAdd24Regular />}
              buttonAriaLabel={placeholder}
              title={placeholder}
              options={options}
              onToggleOption={onToggleAttribute}
              multiselect
              buttonAppearance="subtle"
            />
          ) : null}
        </div>
        {selectedAttributes.length > 0 ? (
          <ul className={styles.selectedList}>
            {selectedAttributes.map((logicalName) => (
              <li key={logicalName} className={styles.selectedItem}>
                {showQuickRemove ? (
                  <Button
                    appearance="subtle"
                    icon={<DeleteDismiss24Regular />}
                    aria-label={`Remove attribute ${logicalName}`}
                    title="Remove attribute"
                    onClick={() => onToggleAttribute(logicalName, false)}
                  />
                ) : null}
                <span className={styles.selectedItemText}>{attributesByLogicalName.get(logicalName) ?? logicalName}</span>
              </li>
            ))}
          </ul>
        ) : (
          <Text className={styles.emptyState}>{emptyLabel}</Text>
        )}
      </div>
    </>
  );
}